import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import {
  CommandCentreSection,
  ConsistencyContext,
  isContextPairRejected,
  isNotFoundError,
  isPreReleaseA,
  isStaleContextError,
  markPreReleaseA,
  subscribeReleaseMode,
  useGetHoldingsSummaryQuery,
} from "../../../../services";
import { IMMEDIATE_RESTARTS, SECTION_RETRY_DELAY_MS } from "../constants";
import { useSectionRetry } from "./useSectionRetry";
import {
  Consistency,
  DisplayedSection,
  SectionGeneration,
  UseLoadGenerationProps,
  UseLoadGenerationReturn,
} from "./types";

type DisplayedSections = Partial<
  Record<CommandCentreSection, DisplayedSection>
>;

export const UNVERSIONED_GENERATION: SectionGeneration = {
  context: undefined,
  generation: 0,
  isReady: true,
  handleSectionError: () => false,
  reportDisplayed: () => undefined,
};

// The summary establishes the pair unversioned; the other sections supply it, and a stale answer from any of them restarts all.
export const useLoadGeneration = ({
  investmentId,
  accountId,
  isLedger,
}: UseLoadGenerationProps): UseLoadGenerationReturn => {
  const preReleaseA = useSyncExternalStore(subscribeReleaseMode, isPreReleaseA);
  const [scopeAccountId, setScopeAccountId] = useState(accountId);
  const [isRemoved, setIsRemoved] = useState(false);
  const [generation, setGeneration] = useState(0);
  const [isRestarting, setIsRestarting] = useState(false);
  const [displayed, setDisplayed] = useState<DisplayedSections>({});
  const generationRef = useRef(generation);
  const restartStreak = useRef(0);
  const restartTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  if (scopeAccountId !== accountId) {
    setScopeAccountId(accountId);
    setIsRemoved(false);
    setIsRestarting(false);
    setDisplayed({});
  }

  useEffect(
    () => () => {
      clearTimeout(restartTimer.current);
      restartStreak.current = 0;
    },
    [accountId],
  );

  const isVersioned = isLedger && accountId !== undefined;
  const summary = useGetHoldingsSummaryQuery(
    { accountId: accountId ?? 0, investmentId },
    { skip: !isVersioned || isRemoved },
  );
  const summaryData = isVersioned ? summary.currentData : undefined;
  const pairVersion = summaryData?.accountVersion ?? null;
  const pairDate = summaryData?.businessDate ?? null;
  const summaryFailed = isVersioned && summary.isError;
  useSectionRetry(isVersioned ? summary.error : undefined, summary.refetch);

  // A summary that answers without a pair comes from a server that predates the contract.
  useEffect(() => {
    if (summaryData && (pairVersion === null || pairDate === null)) {
      markPreReleaseA();
    }
  }, [summaryData, pairVersion, pairDate]);

  useEffect(() => {
    if (isNotFoundError(summary.error)) {
      setIsRemoved(true);
    }
  }, [summary.error]);

  const context = useMemo<ConsistencyContext | undefined>(() => {
    const isEstablished =
      isVersioned &&
      !preReleaseA &&
      !isRestarting &&
      !summaryFailed &&
      pairVersion !== null &&
      pairDate !== null;
    return isEstablished
      ? { accountVersion: pairVersion, businessDate: pairDate }
      : undefined;
  }, [
    isVersioned,
    preReleaseA,
    isRestarting,
    summaryFailed,
    pairVersion,
    pairDate,
  ]);

  const refetchSummary = summary.refetch;
  // The ref moves before the re-render, so a second stale answer from the same generation can't restart twice.
  const restart = useCallback(() => {
    generationRef.current += 1;
    setGeneration(generationRef.current);
    setIsRestarting(true);
    restartStreak.current += 1;
    const reestablish = () => {
      void Promise.resolve(refetchSummary()).finally(() => {
        setIsRestarting(false);
      });
    };
    // A section that keeps answering stale (a quote-refresh burst, a server bug) must not spin the page.
    if (restartStreak.current > IMMEDIATE_RESTARTS) {
      restartTimer.current = setTimeout(reestablish, SECTION_RETRY_DELAY_MS);
    } else {
      reestablish();
    }
  }, [refetchSummary]);

  const handleSectionError = useCallback(
    (error: unknown, queryGeneration: number) => {
      if (isNotFoundError(error)) {
        setIsRemoved(true);
        return true;
      }
      if (isContextPairRejected(error)) {
        markPreReleaseA();
        return true;
      }
      if (isStaleContextError(error)) {
        if (queryGeneration === generationRef.current) {
          restart();
        }
        return true;
      }
      return false;
    },
    [restart],
  );

  const reportDisplayed = useCallback(
    (section: CommandCentreSection, shown: DisplayedSection | null) => {
      setDisplayed((previous) => {
        const current = previous[section];
        const isUnchanged =
          current?.accountVersion === shown?.accountVersion &&
          current?.accountId === shown?.accountId;
        return isUnchanged
          ? previous
          : { ...previous, [section]: shown ?? undefined };
      });
    },
    [],
  );

  let isReady: boolean;
  if (isRemoved) {
    isReady = false;
  } else if (!isLedger) {
    isReady = true;
  } else if (accountId === undefined) {
    isReady = false;
  } else if (preReleaseA) {
    isReady = true;
  } else {
    // A failed summary degrades the sections to unversioned reads rather than leaving them waiting forever.
    isReady = context !== undefined || (summaryFailed && !isRestarting);
  }

  // History echoes its own account id; a version from another account says nothing about this one.
  const shownSections = Object.values(displayed).filter(
    (shown): shown is DisplayedSection =>
      shown !== undefined &&
      (shown.accountId === null || shown.accountId === accountId),
  );
  let consistency: Consistency;
  if (!isLedger || shownSections.length === 0) {
    consistency = "CONSISTENT";
  } else if (context !== undefined) {
    consistency = shownSections.every(
      (shown) => shown.accountVersion === context.accountVersion,
    )
      ? "CONSISTENT"
      : "UPDATING";
  } else if (!isRestarting && (preReleaseA || summaryFailed)) {
    consistency = "UNVERSIONED";
  } else {
    consistency = "UPDATING";
  }

  const isSettled =
    context !== undefined &&
    shownSections.length > 0 &&
    consistency === "CONSISTENT";
  useEffect(() => {
    if (isSettled) {
      restartStreak.current = 0;
    }
  }, [isSettled]);

  const freshness = summaryData?.freshness ?? null;
  const summaryResult = summaryData?.result ?? null;
  const summaryRefreshFailed =
    isVersioned &&
    summary.error !== undefined &&
    !isNotFoundError(summary.error);
  const isSummaryRefreshing = isVersioned && summary.isFetching;

  return useMemo(
    () => ({
      context,
      generation,
      isReady,
      handleSectionError,
      reportDisplayed,
      isRemoved,
      consistency,
      freshness,
      summary: summaryResult,
      summaryRefreshFailed,
      isSummaryRefreshing,
      retrySummary: refetchSummary,
    }),
    [
      context,
      generation,
      isReady,
      handleSectionError,
      reportDisplayed,
      isRemoved,
      consistency,
      freshness,
      summaryResult,
      summaryRefreshFailed,
      isSummaryRefreshing,
      refetchSummary,
    ],
  );
};
